
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext'; 
import { getBrandingSettings, getModuleSettings, getLegalPageSettings, getPortfolioByOwner, getSaaSLandingContent } from '../../services/dataService';
import { getAppMode } from '../../utils/config';
import { BrandingSettings, ModuleSettings, LegalPageSettings } from '../../types';
import { AnnouncementBar } from '../AnnouncementBar';
import { BottomNavItem } from './BottomNavItem';
import { GlobalFooter } from './GlobalFooter';
import { CookieConsent } from '../CookieConsent'; 
import { LayoutDashboard, LogIn, Home, Briefcase, Mic2, Mail, Info, CalendarDays } from 'lucide-react';
import { DOMAIN_CONFIG } from '../../config/domainConfig';
import { synczstyle } from '../../utils/synczstyle';

const DEFAULT_MODULES: ModuleSettings = {
    enableBookings: true,
    enableProjects: true,
    enablePortfolio: true,
    enableTeam: true,
    enableFiles: true,
    enableContact: true,
    enableWorkshops: true,
    enableMarketing: true,
    enableCRM: true,
    enableFinances: true,
    enableLegal: true,
    enableSyncZAuth: true,
    enableSyncZStorage: true,
    enableSyncZDatabase: true,
    enableSyncZWorkers: true,
    enableSyncZRealtime: true
};

const DEFAULT_BRANDING: BrandingSettings = {
    appTitle: 'SyncZ Portfolio',
    developerName: 'SyncZ Ltd'
};

const DEFAULT_LAYOUT = { 
    showAbout: true, 
    showCv: true, 
    showPortfolio: true, 
    showServices: true,
    showWorkshops: true,
    showContact: true
};

export const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { tenant } = useTenant(); 
  const location = useLocation();
  
  // STATE - Volatile React memory loaded live from backend/PostgreSQL
  const [branding, setBranding] = useState<BrandingSettings>(DEFAULT_BRANDING);
  const [modules, setModules] = useState<ModuleSettings>(DEFAULT_MODULES);
  const [legal, setLegal] = useState<LegalPageSettings>({});
  const [layoutConfig, setLayoutConfig] = useState(DEFAULT_LAYOUT);
  
  // Transition States
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [navReady, setNavReady] = useState(true);

  const [appMode, setAppMode] = useState<'PORTFOLIO' | 'MARKETPLACE' | 'PORTAL'>(() => getAppMode());
  const isPortalMode = appMode === 'PORTAL';
  const isMarketplaceMode = appMode === 'MARKETPLACE';
  const isPortfolioMode = appMode === 'PORTFOLIO';

  useEffect(() => {
    const handleModeUpdate = () => {
      setAppMode(getAppMode());
    };
    window.addEventListener('syncz_marketplace_updated', handleModeUpdate);
    window.addEventListener('syncz_platform_updated', handleModeUpdate);
    return () => {
      window.removeEventListener('syncz_marketplace_updated', handleModeUpdate);
      window.removeEventListener('syncz_platform_updated', handleModeUpdate);
    };
  }, []);

  useEffect(() => {
    const init = async () => {
        try {
            // Live Fetch from PostgreSQL
            const [m, l] = await Promise.all([getModuleSettings(), getLegalPageSettings()]);
            
            if (m) {
                setModules(m);
            }
            if (l) {
                setLegal(l);
            }

            let newBranding = branding;
            let newLayout = { ...layoutConfig };

            const ownerIdToFetch = (tenant && tenant.ownerId && tenant.id !== DOMAIN_CONFIG.STANDALONE_TENANT_ID) ? tenant.ownerId : user?.uid;

            if (ownerIdToFetch) {
                try {
                    const portfolio = await getPortfolioByOwner(ownerIdToFetch);
                    if (portfolio) {
                         newBranding = {
                             appTitle: portfolio.name || tenant?.name || branding.appTitle,
                             logoUrl: portfolio.logoUrl || branding.logoUrl,
                             heroVideoUrl: portfolio.heroVideoUrl || '', 
                             developerName: 'SyncZ Ltd',
                             developerUrl: `https://${DOMAIN_CONFIG.SAAS_ROOT}`,
                             faviconUrl: portfolio.logoUrl || branding.faviconUrl 
                         };
                         newLayout = {
                             showAbout: portfolio.showAbout !== false,
                             showCv: portfolio.showCv !== false,
                             showPortfolio: portfolio.showPortfolio !== false,
                             showServices: portfolio.showServices !== false,
                             showWorkshops: portfolio.showWorkshops !== false,
                             showContact: portfolio.showContact !== false
                         };
                    } else if (tenant?.name) {
                         newBranding = { appTitle: tenant.name, developerName: 'SyncZ Ltd' };
                    }
                } catch (err) {
                    console.warn("Portfolio fetch failed, using fallback branding", err);
                }
            } 
            else {
                const [b, saasContent] = await Promise.all([
                    getBrandingSettings(),
                    getSaaSLandingContent()
                ]);
                if (b) newBranding = b;
                newLayout.showCv = false; 
                if (saasContent && saasContent.aboutPage) {
                    newLayout.showAbout = saasContent.aboutPage.showOnNav !== false;
                }
            }
            
            setBranding(newBranding);
            setLayoutConfig(newLayout);
            setNavReady(true);

        } catch(e) { console.error("Failed to load settings", e); }
    };
    
    init();

    const handlePortfolioUpdate = (e: any) => {
        if (e.detail) {
            const p = e.detail;
            const updatedLayout = {
                showAbout: p.showAbout !== false,
                showCv: p.showCv !== false,
                showPortfolio: p.showPortfolio !== false,
                showServices: p.showServices !== false,
                showWorkshops: p.showWorkshops !== false,
                showContact: p.showContact !== false
            };
            setLayoutConfig(updatedLayout);
        }
    };
    window.addEventListener('syncz_portfolio_updated', handlePortfolioUpdate);
    return () => window.removeEventListener('syncz_portfolio_updated', handlePortfolioUpdate);
  }, [tenant, user]);

  // Dynamic Favicon Injection
  useEffect(() => {
      const url = branding.faviconUrl || '/favicon.ico';
      if (url) {
          let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
          if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.getElementsByTagName('head')[0].appendChild(link);
          }
          link.href = url;
      }
  }, [branding.faviconUrl]);

  let pathPrefix = '';
  if (isMarketplaceMode && tenant?.subdomain) {
      if (location.pathname.startsWith(`/t/${tenant.subdomain}`)) {
          pathPrefix = `/t/${tenant.subdomain}`;
      }
  }

  const getLink = (path: string) => {
      if (path === '/') return pathPrefix || '/'; 
      return `${pathPrefix}${path}`;
  };

  const isActive = (path: string) => location.pathname === getLink(path);

  const isPublicDisabled = Boolean(modules.disablePublicPages && !user);
  const showFullNav = !isPublicDisabled;
  const showAbout = !isPublicDisabled && layoutConfig.showAbout;
  const showWork = !isPublicDisabled && modules.enablePortfolio && layoutConfig.showPortfolio;
  const showServices = !isPublicDisabled && modules.enableProjects && layoutConfig.showServices;
  const showWorkshops = !isPublicDisabled && modules.enableWorkshops && layoutConfig.showWorkshops;
  const showContact = !isPublicDisabled && modules.enableContact && layoutConfig.showContact;

  const appTitle = isMarketplaceMode && !tenant ? 'SYNCZ MARKETPLACE' : (isPortalMode ? 'SYNCZ PORTAL' : (branding.appTitle || 'SYNCZ PORTFOLIO'));

  // Define Links Array for Cleaner Rendering
  const desktopLinks = (
      <div className="hidden md:flex items-center gap-8 text-xs font-bold uppercase tracking-widest animate-in fade-in slide-in-from-right-8 duration-700">
        <Link to={getLink('/')} className={`hover:text-white transition-colors ${isActive('/') ? 'text-white' : 'text-neutral-500'}`}>Home</Link>
        {showAbout && <Link to={getLink('/about')} className={`hover:text-white transition-colors ${isActive('/about') ? 'text-white' : 'text-neutral-500'}`}>About</Link>}
        {showWork && <Link to={getLink('/portfolio')} className={`hover:text-white transition-colors ${isActive('/portfolio') ? 'text-white' : 'text-neutral-500'}`}>Work</Link>}
        {showServices && <Link to={getLink('/services')} className={`hover:text-white transition-colors ${isActive('/services') ? 'text-white' : 'text-neutral-500'}`}>Services</Link>}
        {showWorkshops && <Link to={getLink('/workshops')} className={`hover:text-white transition-colors ${isActive('/workshops') ? 'text-white' : 'text-neutral-500'}`}>Workshops</Link>}
        {showContact && <Link to={getLink('/contact')} className={`hover:text-white transition-colors ${isActive('/contact') ? 'text-white' : 'text-neutral-500'}`}>Contact</Link>}
      </div>
  );

  return (
    <div 
        className="min-h-screen bg-black text-white font-sans selection:bg-white selection:text-black flex flex-col"
        style={{
            backgroundColor: branding.themeColors?.background || '#050505',
            color: branding.themeColors?.text || '#e5e5e5',
            '--color-primary': branding.themeColors?.primary || '#ffffff'
        } as React.CSSProperties}
    >
        <header className="sticky top-0 z-50 w-full flex flex-col font-sans shadow-2xl">
            {(isPortfolioMode || tenant) && <AnnouncementBar />}
            
            <nav className="glass-panel border-b border-white/10 px-6 py-4 flex justify-between items-center backdrop-blur-xl bg-black/95 no-print relative min-h-[80px]" role="navigation">
                {/* Logo & Title */}
                <Link to={getLink('/')} className="text-lg md:text-xl font-bold tracking-[0.2em] uppercase z-50 flex items-center gap-3" aria-label="Home">
                    {(branding.logoUrl || '/logo.png') ? (
                         <img src={branding.logoUrl || '/logo.png'} alt={branding.appTitle} className="h-8 md:h-10 w-auto object-contain" width="40" height="40" />
                    ) : (
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-white text-black rounded-sm flex items-center justify-center font-black text-sm md:text-lg shrink-0">
                            {(appTitle).charAt(0)}
                        </div>
                    )}
                    {/* Hide title on mobile if Welcome is showing to prevent overlap */}
                    <span className={`inline text-sm md:text-lg transition-opacity duration-500 ${isFirstVisit && !navReady ? 'opacity-0 md:opacity-100' : 'opacity-100'}`}>
                        {appTitle}
                    </span>
                </Link>

                {/* Center / Right: Logic for Welcome vs Links */}
                <div className="flex-1 flex justify-center md:justify-center">
                    {!navReady ? (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none animate-pulse">
                            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.3em] text-white/80">
                                Welcome to {appTitle}
                            </span>
                        </div>
                    ) : (
                        showFullNav && desktopLinks
                    )}
                </div>

                {/* Right: Dashboard / Login */}
                <div className="flex items-center gap-4 z-50">
                     {user ? (
                        <Link to={getLink('/dashboard')} aria-label="Dashboard" className={synczstyle('primary', 'px-4 md:px-6 py-2 flex items-center gap-2')}>
                            <LayoutDashboard size={14} className="md:hidden" /><span>Dashboard</span>
                        </Link>
                     ) : (
                        <Link to={getLink('/login')} aria-label="Login" className={synczstyle('primary', 'px-4 md:px-6 py-2 flex items-center gap-2')}>
                            <LogIn size={14} className="md:hidden" /><span>Login</span>
                        </Link>
                     )}
                </div>
            </nav>
        </header>

        <main className="flex-1 w-full">
            {children}
        </main>
        
        <GlobalFooter hasBottomNav={!!(showFullNav && navReady)} />

        {/* Mobile Bottom Nav - Only show when Nav Ready */}
        {showFullNav && navReady && (
            <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 h-20 glass-panel border-t border-white/10 border-x-0 border-b-0 pb-safe bg-black/90 backdrop-blur-xl no-print animate-in slide-in-from-bottom-full duration-700">
                <div className="flex justify-around items-center h-full px-2">
                  <BottomNavItem to={getLink('/')} icon={Home} label="Home" isActive={isActive('/')} />
                  {showAbout && <BottomNavItem to={getLink('/about')} icon={Info} label="About" isActive={isActive('/about')} />}
                  {showWork && <BottomNavItem to={getLink('/portfolio')} icon={Mic2} label="Work" isActive={isActive('/portfolio')} />}
                  {showServices && <BottomNavItem to={getLink('/services')} icon={Briefcase} label="Services" isActive={isActive('/services')} />}
                  {showWorkshops && <BottomNavItem to={getLink('/workshops')} icon={CalendarDays} label="Workshops" isActive={isActive('/workshops')} />}
                  {showContact && <BottomNavItem to={getLink('/contact')} icon={Mail} label="Contact" isActive={isActive('/contact')} />}
                  {user ? (
                    <BottomNavItem to={getLink('/dashboard')} icon={LayoutDashboard} label="Dashboard" isActive={isActive('/dashboard')} />
                  ) : (
                    <BottomNavItem to={getLink('/login')} icon={LogIn} label="Login" isActive={isActive('/login')} />
                  )}
                </div>
            </div>
        )}

        <CookieConsent />
    </div>
  );
};
